import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query/keys'
import { useSession } from '@/features/auth/hooks/useSession'
import { resolveWorkspaceId } from '@/lib/workspace-routing'

type Conversation = { id: string; position?: number; [k: string]: any }
type ConversationListResponse = { items?: Conversation[]; nextCursor?: string }

function reorderResponse(old: any, conversationIds: string[]): any {
  if (!old) return old

  const list: Conversation[] | undefined =
    Array.isArray((old as ConversationListResponse).items)
      ? (old as ConversationListResponse).items
      : undefined

  if (!list) return old

  const byId = new Map(list.map((item) => [item.id, item]))
  const posById = new Map(conversationIds.map((id, idx) => [id, idx]))

  const ordered = conversationIds
    .map((id) => byId.get(id))
    .filter(Boolean) as Conversation[]

  const idSet = new Set(conversationIds)
  const leftover = list.filter((item) => !idSet.has(item.id))

  const merged = [...ordered, ...leftover].map((item) => {
    const position = posById.get(item.id)
    return position === undefined ? item : { ...item, position }
  })

  if (Array.isArray((old as ConversationListResponse).items)) return { ...old, items: merged }
  return old
}

export function useReorderConversationsMutation() {
  const queryClient = useQueryClient()
  const { token, selectedContainer } = useSession()

  return useMutation({
    mutationFn: async (conversationIds: string[]) => {
      const workspaceId = resolveWorkspaceId(selectedContainer)
      if (!workspaceId) {
        throw new Error('No workspace selected')
      }
      await api.workspaces.reorderConversations(token, workspaceId, conversationIds)
    },

    onMutate: async (conversationIds: string[]) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.conversations.lists() })
      const previous = queryClient.getQueriesData({ queryKey: queryKeys.conversations.lists() })

      queryClient.setQueriesData({ queryKey: queryKeys.conversations.lists() }, (old) =>
        reorderResponse(old, conversationIds)
      )

      return { previous }
    },

    onError: (_err, _conversationIds, context) => {
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists(), refetchType: 'inactive' })
    },
  })
}
